/**
 * Utility for counting external network interfaces on device nodes
 *
 * Used to display the [n] label on device nodes in the visualization,
 * where n is the number of network interfaces (NICs, ports) with external connections.
 */

import { VisualizationNode, VisualizationLink } from '../types/visualization';

/**
 * Count network interfaces with external connections for a device
 *
 * Network interfaces are components with type 'port' or 'nic' (based on component choice field)
 * External connections are links to components in different devices
 *
 * @param deviceNode - The device node to count interfaces for
 * @param allNodes - All visualization nodes
 * @param allLinks - All visualization links
 * @returns Count of network interfaces with external connections
 */
export function countExternalNetworkInterfaces(
  deviceNode: VisualizationNode,
  allNodes: VisualizationNode[],
  allLinks: VisualizationLink[]
): number {
  if (deviceNode.type !== 'device') {
    return 0;
  }

  // Find all components within this device
  const deviceComponents = allNodes.filter(n => n.parent === deviceNode.id);

  // Filter to only network interface components (port or nic)
  const networkInterfaces = deviceComponents.filter(component => {
    // Check component metadata for choice/type
    const choice = component.metadata?.componentChoice;
    const componentType = component.metadata?.componentType;

    // Match if choice is 'port' or 'nic', or if componentType contains 'port' or 'nic'
    return (
      choice === 'port' ||
      choice === 'nic' ||
      (componentType && (componentType.includes('port') || componentType.includes('nic')))
    );
  });

  // Count how many network interfaces have external connections
  let externalInterfaceCount = 0;

  for (const netInterface of networkInterfaces) {
    const hasExternalConnection = allLinks.some(link => {
      const linkSource = typeof link.source === 'string' ? link.source : link.source.id;
      const linkTarget = typeof link.target === 'string' ? link.target : link.target.id;

      const isInterfaceSource = linkSource === netInterface.id;
      const isInterfaceTarget = linkTarget === netInterface.id;

      if (isInterfaceSource || isInterfaceTarget) {
        // Check if the other endpoint is outside this device
        const otherEndpoint = isInterfaceSource ? linkTarget : linkSource;

        // External if other endpoint doesn't belong to the same parent device
        if (otherEndpoint.includes('/')) {
          const otherParent = otherEndpoint.split('/')[0];
          return otherParent !== deviceNode.id;
        } else {
          // Direct device connection
          return otherEndpoint !== deviceNode.id;
        }
      }
      return false;
    });

    if (hasExternalConnection) {
      externalInterfaceCount++;
    }
  }

  return externalInterfaceCount;
}
